import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { BuildingIcon, EyeIcon, MailIcon, CalendarIcon } from 'lucide-react'
import { trpc } from '@/main'
import { formatDateLocale } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { SearchHeader } from './-components/api/search-header'
import { Pagination } from './-components/shared/pagination'
import { SimpleListPending } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/account/api/$appId/companies')({
  loaderDeps: ({ search }) => ({ p: search.p }),
  loader: ({ context, params, deps }) => {
    context.queryClient.prefetchQuery(
      trpc.apps.getCompanies.queryOptions({ appClientId: params.appId, search: { q: undefined, p: deps.p } }),
    )
  },
  component: RouteComponent,
  pendingComponent: SimpleListPending,
})

function RouteComponent() {
  const { appId } = Route.useParams()
  const { language } = useTranslation()
  const { t } = useLingui()
  const [query, setQuery] = useState<string | undefined>()
  const { p: page } = useRootSearch()

  const listInput = { appClientId: appId, search: { q: query, p: page } }
  const deferredInput = useDeferredValue(listInput)
  const { data: companies, isFetching } = useSuspenseQuery(trpc.apps.getCompanies.queryOptions(deferredInput))
  // True while the deferred input lags the live one — keeps the dimming through that transition.
  const isStale = isFetching || deferredInput !== listInput

  return (
    <div className="flex flex-col gap-4">
      <SearchHeader query={query ?? ''} onQueryChange={setQuery} count={companies.pagination.count} />

      <div className="flex flex-col gap-3" style={{ opacity: isStale ? 0.6 : undefined }}>
        {companies.list.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <BuildingIcon className="text-muted-foreground/50 size-12" />
            <p className="text-muted-foreground">{t`No companies found`}</p>
          </div>
        )}

        {companies.list.map((elem) => (
          <div key={elem.id} className="bg-card rounded-lg border p-4">
            <div className="flex flex-col gap-3">
              {/* Header */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{elem.name ?? t`Unnamed`}</h3>
                  <code className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">{elem.id}</code>
                </div>

                <div className="text-muted-foreground flex flex-col gap-1 text-xs sm:items-end">
                  {elem.created && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="size-3" />
                      {t`Created`}: {formatDateLocale(elem.created, language)}
                    </span>
                  )}
                  {elem.accessed && (
                    <span className="flex items-center gap-1">
                      <EyeIcon className="size-3" />
                      {t`Accessed`}: {formatDateLocale(elem.accessed, language)}
                    </span>
                  )}
                </div>
              </div>

              {/* Metadata */}
              {elem.email && (
                <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
                  <span className="flex items-center gap-1">
                    <MailIcon className="size-3.5" />
                    {elem.email}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Pagination pagination={companies.pagination} />
    </div>
  )
}
